import { useCallback, useEffect, useRef } from 'react';

import styled from 'styled-components';

import { FieldMappingTextEditor } from '@components/text-editors';
import {
  FieldMappingEditorValue,
  FieldMappingFieldItem,
  FieldMappingGroup,
} from '@components/text-editors/addons/field-mapping';
import { SingleField } from '@components/text-editors/addons/single-field';
import { FieldMappingSidebar } from '@components/text-editors/field-mapping-sidebar';
import { EditorMethods } from '@components/text-editors/text-editor';
import { pxToRem } from '@utils/converters';

import { findCanonicalVariable, getComparatorsForFieldType } from './condition-helpers';
import { useFormBuilderContext } from './context';

export { findCanonicalVariable } from './condition-helpers';

export type JourneyVariablePickerOpener = (
  onPick: (variable: FieldMappingFieldItem) => void,
  currentVariable?: FieldMappingFieldItem
) => void;

export const filterGroupsToConditionVariables = (
  groups: FieldMappingGroup[]
): FieldMappingGroup[] => {
  return groups
    .map((group) => ({
      ...group,
      fields: group.fields.filter(
        (item) => item.type !== undefined && getComparatorsForFieldType(item.type).length > 0
      ),
    }))
    .filter((group) => group.fields.length > 0);
};

export const useJourneyVariablePicker = (
  variableGroups: FieldMappingGroup[],
  fieldMappingId: string
): { open: JourneyVariablePickerOpener; picker: JSX.Element } => {
  const editorRef = useRef<EditorMethods>(null);
  const onPickRef = useRef<((variable: FieldMappingFieldItem) => void) | null>(null);
  const isOpenRef = useRef(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const { openOverlayPanel, closeOverlayPanel } = useFormBuilderContext();

  // The editor's insertContent/replaceContent re-assert DOM focus after this
  // hook's handlers run, which would park keyboard focus inside the hidden
  // host. `inert` blocks focus entirely while programmatic content
  // transactions (and their onChange) still go through. Set via a callback
  // ref so it applies whenever the host mounts (a mount-once effect would
  // miss a conditionally-rendered picker), and via attribute because React
  // 17's JSX has no `inert` prop.
  const setInertHost = useCallback((node: HTMLDivElement | null) => {
    node?.setAttribute('inert', '');
  }, []);

  const filteredGroups = filterGroupsToConditionVariables(variableGroups);

  // Keep a ref to the latest filteredGroups so the memoized `open` callback
  // (which captures the closure at creation time) always sees the current
  // groups even if variableGroups load in async after the picker mounts.
  const filteredGroupsRef = useRef(filteredGroups);
  filteredGroupsRef.current = filteredGroups;

  const resetPickerState = useCallback(() => {
    isOpenRef.current = false;
    onPickRef.current = null;
  }, []);

  const closePicker = useCallback(() => {
    resetPickerState();
    closeOverlayPanel?.();
  }, [resetPickerState, closeOverlayPanel]);

  const handleChange = (value: FieldMappingEditorValue) => {
    const variables = value.variables ?? {};
    const variable = Object.values(variables)[0];
    if (!variable || !onPickRef.current) return;

    // Resolve to the canonical FieldMappingFieldItem from variableGroups for the
    // clean label (without action prefix) and options. The icon fields are React
    // elements that must be dropped: the condition stores the variable verbatim
    // and the form save serializes it with JSON.stringify, which throws on a
    // React element's circular fiber refs.
    const resolved = findCanonicalVariable(filteredGroupsRef.current, variable) ?? variable;
    const { icon, iconRight, ...canonical } = resolved;
    const pick = onPickRef.current;
    resetPickerState();
    editorRef.current?.replaceContent('');
    // Close before handing control to the caller, so anything onPick opens
    // (including this picker) isn't torn down by a trailing close.
    closeOverlayPanel?.();
    pick(canonical);
  };

  const open = useCallback<JourneyVariablePickerOpener>(
    (onPick, currentVariable) => {
      if (!openOverlayPanel) return;
      onPickRef.current = onPick;
      isOpenRef.current = true;
      // The overlay-panel slot keeps the sidebar adjacent to the
      // element-properties panel; the standard sidebar mount would overlap it.
      openOverlayPanel({
        render: ({ close }) => (
          <PanelContent ref={panelRef}>
            <FieldMappingSidebar
              editorRef={editorRef}
              variableGroups={filteredGroupsRef.current}
              fieldMappingId={fieldMappingId}
              fieldType="text"
              variable={currentVariable}
              // The insert-as-label split menu makes no sense for picking (a
              // label insert carries no variable) and its portaled panel would
              // land outside panelRef, so a click in it would dismiss us.
              shouldHideMenu
              onClose={() => {
                resetPickerState();
                close();
              }}
            />
          </PanelContent>
        ),
      });
    },
    [fieldMappingId, openOverlayPanel, resetPickerState]
  );

  // Close the picker when the user clicks outside the field-map sidebar.
  // Attached once; gated by isOpenRef so events fire no-ops when the picker
  // is not currently rendered. The mousedown that opens the picker has
  // already finished bubbling by the time React commits + sets isOpenRef
  // to true, so this listener does not race the opening click.
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (!isOpenRef.current) return;
      const target = event.target as Element | null;
      if (!target) return;
      const panel = panelRef.current;
      // The overlay-panel slot is last-write-wins; a null panel means another
      // feature replaced ours. Disarm instead of closing a panel we don't own.
      if (!panel) {
        resetPickerState();
        return;
      }
      // A native scrollbar press targets the scroll wrapper around the panel
      // (the element that owns the scrollbar), which is not a descendant of
      // the panel — check against the wrapper so grabbing the scrollbar
      // doesn't dismiss the picker.
      const scrollContainer = panel.parentElement ?? panel;
      if (!scrollContainer.contains(target)) {
        closePicker();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [closePicker, resetPickerState]);

  // The overlay panel outlives this hook's tree, so close it on unmount —
  // but only if we still own the slot (panelRef goes null when another
  // feature's panel replaces ours).
  useEffect(
    () => () => {
      if (isOpenRef.current && panelRef.current) closeOverlayPanel?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const picker = (
    <PickerHost ref={setInertHost} aria-hidden>
      <FieldMappingTextEditor
        ref={editorRef}
        onChange={handleChange}
        variableGroups={filteredGroups}
        fieldMappingId={fieldMappingId}
        plaintext
        singleLine
        hideLabel
        extensions={[SingleField]}
      />
    </PickerHost>
  );

  return { open, picker };
};

// The sidebar's own container is height: 100%; keep the chain intact.
const PanelContent = styled.div({
  height: '100%',
});

const PickerHost = styled.div({
  position: 'absolute',
  width: pxToRem(1),
  height: pxToRem(1),
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
  clip: 'rect(0 0 0 0)',
});
